import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';
import SmallScreens from './components/SmallScreens';
import ListingsGrid from './components/ListingsGrid';
import MyPosts from './components/MyPosts';
import Favourites from './components/Favourites';
import PostAdPage from './pages/PostAdPage';
import Ad from './components/Ad'; 
import TeamUpAd from './components/TeamUpAd';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { auth, db } from './firebase'; 
import { signOut } from 'firebase/auth';
import PostingTeamUp from './pages/PostingTeamUp';
import LoginModal from './components/LoginModal';
import RegisterModal from './components/RegisterModal';
import ForgotPasswordModal from './components/ForgotPasswordModal';
import AdBlockerModal from './components/AdBlockerModal';


// ─── In-App Browser Detection ───────────────────────────────────────────────
const ua = navigator.userAgent;
const isInAppBrowser = /FBAN|FBAV|Instagram|Messenger|WhatsApp/i.test(ua);

function InAppBrowserWarning({ currentLanguage }) {
  const isAr = currentLanguage === 'ar';
  return (
    <div className="inapp-overlay" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="inapp-card">
        <div className="inapp-icon">🌐</div>
        <h2 className="inapp-title">
          {isAr ? 'افتح الموقع في المتصفح' : 'Open in Your Browser'}
        </h2>
        <p className="inapp-desc">
          {isAr
            ? 'بعض المميزات مش هتشتغل صح في المتصفح الداخلي. افتح الموقع في Chrome أو Safari عشان تجربة أفضل.'
            : 'Some features may not work properly in this in-app browser. Open in Chrome or Safari for the full experience.'}
        </p>
        <div className="inapp-steps">
          <div className="inapp-step">
            <span className="inapp-step-icon">⋮</span>
            <span>{isAr ? 'اضغط على القائمة' : 'Tap the menu'}</span>
          </div>
          <div className="inapp-arrow">→</div>
          <div className="inapp-step">
            <span className="inapp-step-icon">🔗</span>
            <span>{isAr ? 'افتح في المتصفح' : 'Open in browser'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
// ────────────────────────────────────────────────────────────────────────────

function App() {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('appLanguage');
    return savedLanguage || 'en';
  });

  const [user, setUser] = useState(null);
  const [userFullName, setUserFullName] = useState('');
  const [authLoading, setAuthLoading] = useState(true);

  const contentRef = useRef(null);

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);

  const openLoginModal = () => {
    setShowLoginModal(true);
    setShowRegisterModal(false);
    setShowForgotModal(false);
  };

  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');
  const [activeHeroFilter, setActiveHeroFilter] = useState('all');
  // Controls visibility of the (non-functional) filter button in the mobile header —
  // only shown while an expanded "Show More" category grid is open.
  const [expandedCategory, setExpandedCategory] = useState(null);

  const getLocalizedText = useCallback((arText, enText) => {
    return currentLanguage === 'ar' ? arText : enText;
  }, [currentLanguage]);

  useEffect(() => {
    if (activeHeroFilter !== 'all' && contentRef.current) {
      setTimeout(() => {
        contentRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100); 
    }
  }, [activeHeroFilter]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        if (currentUser.emailVerified) {
          try {
            const verifiedDocRef = doc(db, 'verified_users', currentUser.uid);
            const verifiedSnap = await getDoc(verifiedDocRef);

            let fullName = 'مستخدم';

            if (verifiedSnap.exists()) {
              const data = verifiedSnap.data();
              if (data.fullName) fullName = data.fullName.trim();
              else if (data.firstName && data.lastName) fullName = `${data.firstName.trim()} ${data.lastName.trim()}`;
              else if (data.displayName) fullName = data.displayName.trim();
              else if (data.name) fullName = data.name.trim();
              else if (data.firstName) fullName = data.firstName.trim();
            } else {
              const unverifiedDocRef = doc(db, 'unverified_users', currentUser.uid);
              const unverifiedSnap = await getDoc(unverifiedDocRef);

              if (unverifiedSnap.exists()) {
                const userData = unverifiedSnap.data();

                await setDoc(verifiedDocRef, {
                  ...userData,
                  verifiedAt: new Date().toISOString(),
                  last_login_at: new Date().toISOString(),
                });

                await deleteDoc(unverifiedDocRef);

                if (userData.fullName) fullName = userData.fullName.trim();
                else if (userData.firstName && userData.lastName) fullName = `${userData.firstName.trim()} ${userData.lastName.trim()}`;
                else if (userData.firstName) fullName = userData.firstName.trim();
              }
            }

            setUser(currentUser);
            setUserFullName(fullName);

          } catch (error) {
            console.error("Error handling verified user data:", error);
            setUser(currentUser);
            setUserFullName('مستخدم');
          }
        } else {
          setUser(null);
          setUserFullName('');
        }
      } else {
        setUser(null);
        setUserFullName('');
      }
      
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('dir', currentLanguage === 'ar' ? 'rtl' : 'ltr');
    document.documentElement.lang = currentLanguage;

    const titleElement = document.querySelector('title');
    if (titleElement) {
      if (currentLanguage === 'ar') {
        titleElement.textContent = titleElement.getAttribute('data-ar') || "Roommate";
      } else {
        titleElement.textContent = titleElement.getAttribute('data-en') || "Roommate";
      }
    }

    localStorage.setItem('appLanguage', currentLanguage);
  }, [currentLanguage]);

  const toggleLanguage = () => {
    setCurrentLanguage(prevLang => (prevLang === 'ar' ? 'en' : 'ar'));
  };

  const updateCityAndRegion = (newCity, newRegion) => {
    setCity(newCity || '');
    setRegion(newRegion || '');
  };

  // Hero tab click: set active tab and leave expanded view (same as Back)
  const handleHeroFilterChange = (type) => {
    setActiveHeroFilter(type);
    setExpandedCategory(null);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const [selectedLocation, setSelectedLocation] = useState(null);

  // ── Show warning if opened inside Facebook / Messenger / Instagram / WhatsApp ──
  if (isInAppBrowser) {
    return <InAppBrowserWarning currentLanguage={currentLanguage} />;
  }

  const headerProps = {
    currentLanguage,
    toggleLanguage,
    updateCityAndRegion,
    user,
    userFullName,
    onLogout: handleLogout,
    updateSelectedLocation: setSelectedLocation,
  };

  return (
    <Router>
      <div className="App">
        {/* Header Route (فقط للصفحات اللي معندهاش هيدر جوه الـ element بتاعها، زي /my-posts و /favourites) */}
        <Routes>
          <Route path="/" element={null} />
          <Route path="/post-ad" element={null} />
          <Route path="/ad/:id" element={null} />
          <Route path="/post-teamup" element={null} />
          <Route path="/teamup/:id" element={null} />
          <Route path="*" element={
            <SmallScreens 
              {...headerProps} 
              isHeaderOnly={true}
              activeHeroFilter={activeHeroFilter}
              onHeroFilterChange={handleHeroFilterChange}
            /> 
          } />
        </Routes>

        {/* Main Content Route */}
        <Routes>
          <Route
            path="/"
            element={
              <SmallScreens 
                {...headerProps} 
                isHeaderOnly={false}
                activeHeroFilter={activeHeroFilter}
                onHeroFilterChange={handleHeroFilterChange}
              >
                <ListingsGrid
                  currentLanguage={currentLanguage}
                  getLocalizedText={getLocalizedText}
                  city={city}
                  region={region}
                  selectedLocation={selectedLocation}
                  user={user}
                  isMobile={true}
                  activeHeroFilter={activeHeroFilter}
                  onHeroFilterChange={setActiveHeroFilter}
                  expandedCategory={expandedCategory}
                  onExpandedCategoryChange={setExpandedCategory}
                />
              </SmallScreens>
            }
          />
          <Route path="/post-ad" element={<PostAdPage currentLanguage={currentLanguage} getLocalizedText={getLocalizedText} />} />
          <Route path="/ad/:id" element={<Ad currentLanguage={currentLanguage} getLocalizedText={getLocalizedText} onOpenLogin={openLoginModal} />} />
          <Route path="/teamup/:id" element={<TeamUpAd currentLanguage={currentLanguage} getLocalizedText={getLocalizedText} onOpenLogin={openLoginModal} />} />
          <Route path="/my-posts" element={<MyPosts currentLanguage={currentLanguage} getLocalizedText={getLocalizedText} user={user} authLoading={authLoading} isMobile={true} />} />
          <Route path="/post-teamup" element={<PostingTeamUp currentLanguage={currentLanguage} getLocalizedText={getLocalizedText} />} />
          <Route path="/favourites" element={<Favourites currentLanguage={currentLanguage} getLocalizedText={getLocalizedText} user={user} authLoading={authLoading} isMobile={true} />} />
        </Routes>

        {/* Global Modals */}
        {showLoginModal && (
          <LoginModal
            currentLanguage={currentLanguage}
            getLocalizedText={getLocalizedText}
            onClose={(type) => {
              setShowLoginModal(false);
              if (type === 'register') setShowRegisterModal(true);
              if (type === 'forgot') setShowForgotModal(true);
            }}
          />
        )}

        {showRegisterModal && (
          <RegisterModal
            currentLanguage={currentLanguage}
            getLocalizedText={getLocalizedText}
            onClose={(type) => {
              setShowRegisterModal(false);
              if (type === 'login') setShowLoginModal(true);
            }}
          />
        )}

        {showForgotModal && (
          <ForgotPasswordModal
            currentLanguage={currentLanguage}
            getLocalizedText={getLocalizedText}
            onClose={() => setShowForgotModal(false)}
          />
        )}

        <AdBlockerModal 
          currentLanguage={currentLanguage} 
          getLocalizedText={getLocalizedText} 
        />

      </div>
    </Router>
  );
}

export default App;